import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import api from '../../../services/api';
import usePerformanceSettings from '../../../hooks/usePerformanceSettings';
import { dateFilterColumn } from '../../../utils/dateFilterColumn';
import { extractListData } from '../../../utils/Utils';

// Task 33 — PM Konfigürasyon sayfası: 4 sekme (Ayarlar / Yetkinlikler /
// 360° Form Şablonları / Bildirim Şablonları). Gerçek rotalar (grep ile doğrulanmış):
//   GET/PUT v1/performance-settings                                  (Task 1/26)
//   GET/POST/PUT/DELETE v1/competencies(/{id})                       (Dilim 0)
//   GET/POST/PUT/DELETE v1/feedback-forms(/{id})                     (Task 10)
//   POST/PUT/DELETE v1/feedback-forms/{id}/questions(/{questionId})  (Task 10)
//   PUT v1/feedback-forms/{id}/questions/reorder                     (Task 10)
//   GET/PUT/DELETE v1/notification-templates/{eventKey}               (Task 16)
//   PUT v1/notification-templates/{eventKey}/enabled {isEnabled}      (aç/kapa)

// ─────────────────────────────────────────────────────────────────────────
// Sekme 1 — Ayarlar
// ─────────────────────────────────────────────────────────────────────────

// Modül-içi paylaşılan önbelleği (usePerformanceSettings) okuma kaynağı olarak
// kullanır; PUT sonrası refresh() çağrısı hem bu sayfayı hem de Hedeflerim/Ekip
// Hedeflerim gibi diğer tüketicileri güncel tutar (bkz. hook'un kendi yorumu —
// "Task 1'in konfigürasyon ekranı" tam olarak bu sayfadır).
export const usePerformanceSettingsApi = () => {
  const { settings, loading, refresh } = usePerformanceSettings();
  const [saving, setSaving] = useState(false);

  const save = useCallback(async (payload) => {
    setSaving(true);
    try {
      await api.put('/v1/performance-settings', payload);
      await refresh();
      return true;
    } catch {
      return false;
    } finally {
      setSaving(false);
    }
  }, [refresh]);

  return { settings, fetching: loading, saving, save };
};

// ─────────────────────────────────────────────────────────────────────────
// Sekme 2 — 360° Form Şablonları
// ─────────────────────────────────────────────────────────────────────────

// P1 — seçici etiketi: grup tanımlıysa "GroupName — Name" (soru ekleme
// seçicisinde gruplu görünüm; FeedbackFormsTab bu async'i kullanır).
const competencyLabel = (c) => (c.groupName ? `${c.groupName} — ${c.name}` : c.name);

export function buildCompetencyAsync() {
  return {
    onSearch: async (q) => {
      const params = q ? { 'name[like]': q, pageSize: 20 } : { pageSize: 20 };
      const res = await api.get('/v1/competencies', { params });
      const r = res.data?.data;
      const items = Array.isArray(r) ? r : (r?.data || []);
      return items.map((c) => ({ id: c.id, name: competencyLabel(c) }));
    },
    onGetById: async (id) => {
      if (!id) return null;
      const res = await api.get(`/v1/competencies/${id}`);
      const c = res.data?.data;
      return c ? { id: c.id, name: competencyLabel(c) } : null;
    },
  };
}

export const competencyAsync = buildCompetencyAsync();

// Faz E (Task 5, spec §4) — form editörünün "pozisyon yetkinliklerinden öner"
// listesi, tıklanan yetkinliğin AÇIKLAMASINI da soru taslağı metnine katar.
// `competencyAsync.onGetById` YALNIZ {id,name} döner (AsyncGenericSelect'in
// etiket sözleşmesi — bkz. yukarısı), description taşımaz; bu yüzden ayrı,
// hafif bir fetch (FeedbackFormsTab.js'in tek tüketicisi).
export const fetchCompetencyDescription = async (id) => {
  if (!id) return '';
  try {
    const res = await api.get(`/v1/competencies/${id}`);
    return res.data?.data?.description || '';
  } catch {
    return '';
  }
};

// Soru listesindeki CompetencyId'leri (isim taşımaz) satır bazında ada çözer —
// feedback-360/config'teki useEmployeeNames'in bağımsız kopyası (aynı gerekçe:
// repo konvansiyonu paylaşılan hook yerine bağımsız kopya kullanır).
export const useCompetencyNames = (ids) => {
  const [names, setNames] = useState({});
  const namesRef = useRef(names);
  namesRef.current = names;
  const idsKey = JSON.stringify([...new Set((ids || []).filter(Boolean))].sort());

  useEffect(() => {
    const uniqueIds = [...new Set((ids || []).filter(Boolean))];
    const missing = uniqueIds.filter((id) => !(id in namesRef.current));
    if (missing.length === 0) return undefined;

    let cancelled = false;
    Promise.allSettled(missing.map((id) => competencyAsync.onGetById(id)))
      .then((results) => {
        if (cancelled) return;
        setNames((current) => {
          const next = { ...current };
          missing.forEach((id, i) => {
            const r = results[i];
            next[id] = (r.status === 'fulfilled' && r.value?.name) || id;
          });
          return next;
        });
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey]);

  return names;
};

// ─────────────────────────────────────────────────────────────────────────
// Sekme — Yetkinlik Kütüphanesi
// ─────────────────────────────────────────────────────────────────────────

// 2026-08-04 müşteri sorusu üzerine eklendi: 360° form sorusundaki "İlgili
// Yetkinlik" seçicisi /v1/competencies'ten beslenir ama kütüphaneyi yöneten
// ekran PRD cutover'ında (Task 37/38) eski Yetkinlikler sayfasıyla birlikte
// silinmişti — spec ise kütüphanenin YAŞAMASINI öngörüyordu ("Yetkinlikler
// kütüphane olarak kalır", 2026-07-21 design §Menü). Backend CRUD'u cutover'dan
// sağ çıktı; bu hook yalnız o uçları yeni sekmeye bağlar.
export const useCompetenciesApi = (queryParams) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  useEffect(() => {
    const params = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : { pageNumber: 1, pageSize: 10 };
    setFetching(true);
    api.get(`/v1/competencies?${new URLSearchParams(params)}`)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const createCompetency = useCallback(async (payload) => {
    try {
      await api.post('/v1/competencies', payload);
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  const updateCompetency = useCallback(async (id, payload) => {
    try {
      await api.put(`/v1/competencies/${id}`, payload);
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  const deleteCompetency = useCallback(async (id) => {
    try {
      await api.delete(`/v1/competencies/${id}`);
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    {
      name: 'name',
      label: t('performance.competency.name'),
      options: { filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' } },
    },
    // P1 — grup kolonu + sunucu filtresi (groupName[like]).
    {
      name: 'groupName',
      label: t('performance.competency.groupName'),
      options: {
        filter: true, sort: false, filterType: 'textField', filterOptions: { defaultOp: 'like' },
        customBodyRender: (v) => v || '-',
      },
    },
    {
      name: 'competencyType',
      label: t('performance.competency.type'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v) => t(`performance.competency.${v}`),
      },
    },
    {
      name: 'description',
      label: t('performance.common.description'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v) => (v ? (v.length > 80 ? `${v.slice(0, 80)}…` : v) : '-'),
      },
    },
    dateFilterColumn('createDate', t('performance.common.createDate'), lang),
  ], [t, lang]);

  return {
    fetching, data, pagination, columns,
    createCompetency, updateCompetency, deleteCompetency, refresh,
  };
};

export const useFeedbackFormsApi = (queryParams) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  useEffect(() => {
    const params = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : { pageNumber: 1, pageSize: 10 };
    setFetching(true);
    api.get(`/v1/feedback-forms?${new URLSearchParams(params)}`)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  // Faz E (Task 4) — gövde artık salt `name` değil, tam `FeedbackFormRequest`
  // (name + 5 opsiyonel ağırlık override alanı, task-2-report.md §1). İsim
  // korunuyor ("createForm"/"renameForm") ama artık tam payload objesi kabul
  // ediyorlar — çağıran taraf (FeedbackFormsTab.js) drawer'ın kendi taslak
  // state'inden (name + weight*) inşa eder.
  const createForm = useCallback(async (payload) => {
    try {
      await api.post('/v1/feedback-forms', payload);
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  const renameForm = useCallback(async (id, payload) => {
    try {
      await api.put(`/v1/feedback-forms/${id}`, payload);
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  const deleteForm = useCallback(async (id) => {
    try {
      await api.delete(`/v1/feedback-forms/${id}`);
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    {
      name: 'name',
      label: t('performance.settings.forms.nameColumn'),
      options: { filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' } },
    },
    {
      name: 'locked',
      label: t('performance.settings.forms.lockedColumn'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v) => (
          <span style={{ color: v ? '#B02A37' : '#1B7A3E', fontWeight: 600 }}>
            {v ? t('performance.settings.forms.lockedYes') : t('performance.settings.forms.lockedNo')}
          </span>
        ),
      },
    },
    {
      name: 'questionCount',
      label: t('performance.settings.forms.questionCountColumn'),
      options: { filter: false, sort: false },
    },
    dateFilterColumn('createDate', t('performance.common.createDate'), lang),
  ], [t, lang]);

  return { fetching, data, pagination, columns, createForm, renameForm, deleteForm, refresh };
};

export const useFeedbackFormDetailApi = (formId) => {
  const [form, setForm] = useState(null);
  const [fetching, setFetching] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  useEffect(() => {
    if (!formId) {
      setForm(null);
      setFetching(false);
      return;
    }
    setFetching(true);
    api.get(`/v1/feedback-forms/${formId}`)
      .then((res) => setForm(res.data?.data || null))
      .catch(() => setForm(null))
      .finally(() => setFetching(false));
  }, [formId, refreshTrigger]);

  const addQuestion = useCallback(async (payload) => {
    try {
      await api.post(`/v1/feedback-forms/${formId}/questions`, payload);
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [formId, refresh]);

  const updateQuestion = useCallback(async (questionId, payload) => {
    try {
      await api.put(`/v1/feedback-forms/${formId}/questions/${questionId}`, payload);
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [formId, refresh]);

  const removeQuestion = useCallback(async (questionId) => {
    try {
      await api.delete(`/v1/feedback-forms/${formId}/questions/${questionId}`);
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [formId, refresh]);

  // items: [{ id, sortOrder }] — reorder ucu YALNIZ değişen id'leri kabul eder
  // (formdaki TÜM soruları göndermek zorunlu değil, bkz. ReorderAsync).
  const reorderQuestions = useCallback(async (items) => {
    try {
      await api.put(`/v1/feedback-forms/${formId}/questions/reorder`, items);
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [formId, refresh]);

  return { form, fetching, addQuestion, updateQuestion, removeQuestion, reorderQuestions, refresh };
};

// ─────────────────────────────────────────────────────────────────────────
// Sekme 3 — Bildirim Şablonları
// ─────────────────────────────────────────────────────────────────────────

export const useNotificationTemplatesApi = () => {
  const [data, setData] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [saving, setSaving] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  useEffect(() => {
    setFetching(true);
    api.get('/v1/notification-templates')
      .then((res) => setData(Array.isArray(res.data?.data) ? res.data.data : []))
      .finally(() => setFetching(false));
  }, [refreshTrigger]);

  const save = useCallback(async (eventKey, payload) => {
    setSaving(true);
    try {
      await api.put(`/v1/notification-templates/${eventKey}`, payload);
      refresh();
      return true;
    } catch {
      return false;
    } finally {
      setSaving(false);
    }
  }, [refresh]);

  // Boş konu/gövde ile PUT sunucuda reddedilir (subjectRequired/bodyRequired) —
  // varsayılana dönmenin gerçek yolu DELETE'tir (bkz. NotificationTemplateDataAccess
  // .DeleteAsync — override satırını pasifleştirir, GET artık kataloğun
  // varsayılanını döner).
  const resetToDefault = useCallback(async (eventKey) => {
    setSaving(true);
    try {
      await api.delete(`/v1/notification-templates/${eventKey}`);
      refresh();
      return true;
    } catch {
      return false;
    } finally {
      setSaving(false);
    }
  }, [refresh]);

  // Aç/kapa (PUT {eventKey}/enabled). Metin override'ından BAĞIMSIZ: listeyi
  // yeniden çekmek yerine yerel olarak güncellenir (iyimser), hata olursa geri
  // alınır. Mutasyonlar HTTP hatasında reject eder (api.js); 401 ise
  // `{status:false}` ile RESOLVE eder — o yüzden ikisi de başarısızlık sayılır.
  // Toast ATILMAZ: interceptor zaten atıyor.
  const setEnabled = useCallback(async (eventKey, isEnabled) => {
    const apply = (value) => setData((prev) => prev.map((d) => (
      d.eventKey === eventKey ? { ...d, isEnabled: value } : d
    )));
    apply(isEnabled);
    try {
      const res = await api.put(`/v1/notification-templates/${eventKey}/enabled`, { isEnabled });
      if (res?.data?.status === false) {
        apply(!isEnabled);
        return false;
      }
      return true;
    } catch {
      apply(!isEnabled);
      return false;
    }
  }, []);

  return { data, fetching, saving, save, resetToDefault, setEnabled, refresh };
};

// ─────────────────────────────────────────────────────────────────────────
// Sekme — Puan Skalaları (Skorlama Plan 2, Task 10)
// ─────────────────────────────────────────────────────────────────────────

// GET /v1/rating-scales SAYFALANMAZ (RatingScaleDataAccess.ListAsync — tenant'ın
// TÜM aktif skalalarını tek seferde döner, queryParams okumaz). Bu yüzden
// RatingScalesTab DataTable'ı serverSide={false} ile çağırır (RolesPage/
// PoolTagsTab'daki client-side liste deseninin aynısı) — sayfa/sıralama/filtre
// state'i burada YOK, yalnız create/update/delete/copy sonrası refresh().
export const useRatingScalesApi = () => {
  const [data, setData] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  useEffect(() => {
    setFetching(true);
    api.get('/v1/rating-scales')
      .then((res) => setData(Array.isArray(res.data?.data) ? res.data.data : []))
      .finally(() => setFetching(false));
  }, [refreshTrigger]);

  const createScale = useCallback(async (payload) => {
    try {
      await api.post('/v1/rating-scales', payload);
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  const updateScale = useCallback(async (id, payload) => {
    try {
      await api.put(`/v1/rating-scales/${id}`, payload);
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  const deleteScale = useCallback(async (id) => {
    try {
      await api.delete(`/v1/rating-scales/${id}`);
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  // Kilitli skalanın salt-okunur formundaki tek mutasyon: POST {id}/copy —
  // kaynağı değiştirmez, "(Kopya)" adıyla kilitsiz bir kopya üretir (bkz.
  // RatingScaleDataAccess.Copy). Liste refresh() ile yenilenir.
  const copyScale = useCallback(async (id) => {
    try {
      await api.post(`/v1/rating-scales/${id}/copy`);
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  // Analitik Plan 2 — kilitli ölçekte de yalnız bant değiştirilebilir (yalnız İK).
  const setLevelBand = useCallback(async (scaleId, levelId, band) => {
    try {
      await api.put(`/v1/rating-scales/${scaleId}/levels/${levelId}/band`, { band });
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  return { data, fetching, createScale, updateScale, deleteScale, copyScale, setLevelBand, refresh };
};

// ─────────────────────────────────────────────────────────────────────────
// Sekme — Hedef Şablonları (Faz F, Task 4, spec §4)
// ─────────────────────────────────────────────────────────────────────────

// ObTaskTemplatesPage'in (onboarding-task-templates/config) departmentId
// asyncSelect alanının BAĞIMSIZ kopyası (repo konvansiyonu — bkz. bu dosyanın
// başındaki useCompetencyNames yorumu: paylaşılan hook yerine bağımsız kopya
// tercih edilir). Pozisyon için ayrı bir kopya AÇILMADI: GoalTemplatesTab.js
// FeedbackFormsTab.js'in zaten yaptığı gibi `../positions/config`'ten
// positionAsync'i doğrudan import eder.
export const departmentAsync = {
  onSearch: async (q) => {
    const params = q ? { 'name[like]': q, pageSize: 20 } : { pageSize: 20 };
    const res = await api.get('/v1/departments', { params });
    const r = res.data?.data;
    const items = Array.isArray(r) ? r : (r?.data || []);
    return items.map((d) => ({ id: d.id, name: d.name }));
  },
  onGetById: async (id) => {
    if (!id) return null;
    const res = await api.get(`/v1/departments/${id}`);
    const d = res.data?.data;
    return d ? { id: d.id, name: d.name } : null;
  },
};

// GET v1/goal-templates SAYFALANMAZ — düz dizi döner (task-3-report.md §1.4 /
// concern #1: PaginatedList DEĞİL, `ObTaskTemplate.cs`'in AYNI "küçük
// kütüphane" gerekçesi). useRatingScalesApi'nin BİREBİR aynı client-side liste
// deseni: serverSide={false}, yalnız create/update/delete sonrası refresh().
export const useGoalTemplatesApi = () => {
  const [data, setData] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  useEffect(() => {
    setFetching(true);
    api.get('/v1/goal-templates')
      .then((res) => setData(Array.isArray(res.data?.data) ? res.data.data : []))
      .finally(() => setFetching(false));
  }, [refreshTrigger]);

  const createTemplate = useCallback(async (payload) => {
    try {
      await api.post('/v1/goal-templates', payload);
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  const updateTemplate = useCallback(async (id, payload) => {
    try {
      await api.put(`/v1/goal-templates/${id}`, payload);
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  const deleteTemplate = useCallback(async (id) => {
    try {
      await api.delete(`/v1/goal-templates/${id}`);
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  return {
    data, fetching, createTemplate, updateTemplate, deleteTemplate, refresh,
  };
};

// ─────────────────────────────────────────────────────────────────────────
// Sekme — Vekâletler (Faz J, Task 4, spec §4, task-1-report.md §1)
// ─────────────────────────────────────────────────────────────────────────

// Çalışan seçici (veren/vekil + Toplu Ata'nın çalışan çoklu-seçimi) — GENEL
// /v1/employees ucu, continuous-feedback/config.buildEmployeeAsync'in
// BAĞIMSIZ kopyası (repo konvansiyonu: farklı sayfa grubu = bağımsız kopya).
export function buildEmployeeAsync() {
  return {
    onSearch: async (q) => {
      const params = q ? { 'fullName[like]': q, pageSize: 20 } : { pageSize: 20 };
      const res = await api.get('/v1/employees', { params: { ...params, lookup: true } });
      const items = extractListData(res);
      return items.map((e) => ({
        id: e.id,
        name: e.fullName || `${e.firstName || ''} ${e.lastName || ''}`.trim(),
        profilePicture: e.profilePicture,
      }));
    },
    onGetById: async (id) => {
      if (!id) return null;
      const res = await api.get(`/v1/employees/${id}`);
      const e = res.data?.data;
      return e ? { id: e.id, name: e.fullName || `${e.firstName || ''} ${e.lastName || ''}`.trim(), profilePicture: e.profilePicture } : null;
    },
  };
}

export const employeeAsync = buildEmployeeAsync();

// comp-cycles/config'teki StatusBadge ile AYNI imza (color/bg düz prop).
export const Badge = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  background: ${(p) => p.bg};
  color: ${(p) => p.color};
`;

// ═══════ VEKÂLET VERİ KATMANI BURADAN TAŞINDI ═══════
// `useDelegationsApi` / `computeDelegationWindowStatus` / `DELEGATION_WINDOW_COLORS`
// artık `pages/delegations/config.js`tedir: vekâlet platform seviyesine çıktı (uç
// `v1/delegations`, kapsam kolonu) ve Performans'a abone OLMAYAN kiracılar da
// kullanıyor. Performans Ayarları'ndaki Vekâletler sekmesi o gövdeyi
// `lockedScope="performance"` ile sürer.

// ─────────────────────────────────────────────────────────────────────────
// GoalTemplatesTab satır aksiyonu — Toplu Ata (Faz J, Task 4, spec §4,
// task-3-report.md §3)
// ─────────────────────────────────────────────────────────────────────────

// EvaluationPeriodFilterRequest yalnız tek-değerli status[eq] (period-goals/
// config.fetchPeriodsByStatus'un BAĞIMSIZ kopyası — Toplu Ata yalnız `active`
// dönemi kabul eder, task-3-report.md §3 "dönem active şart" kararı).
export const useActivePeriods = () => {
  const [periods, setPeriods] = useState([]);
  const [fetching, setFetching] = useState(true);

  useEffect(() => {
    setFetching(true);
    api.get('/v1/evaluation-periods', { params: { 'status[eq]': 'active', pageSize: 50 } })
      .then((res) => {
        const r = res.data?.data;
        const items = Array.isArray(r) ? r : (r?.data || []);
        setPeriods(items);
      })
      .catch(() => setPeriods([]))
      .finally(() => setFetching(false));
  }, []);

  return { periods, fetching };
};

// POST v1/period-goals/bulk-assign — Data{created, skipped:[{employeeId,reason}]}
// (task-3-report.md §3). Bileşen kendi submitting/result state'ini yönettiği
// için burada bağımsız bir async yardımcı yeterli (hook GEREKMEZ — repo
// emsali: fetchCompetencyDescription).
export const bulkAssignGoals = async (payload) => {
  try {
    const res = await api.post('/v1/period-goals/bulk-assign', payload);
    return res.data?.data || null;
  } catch {
    return null;
  }
};

// (Webhook'lar sekmesi Public API Plan 03 ile Entegrasyonlar sayfasına taşındı —
// kanca ve servis: pages/integrations/config/webhooks.js + services/webhookService.js.)

// ─────────────────────────────────────────────────────────────────────────
// Sekme — Entegrasyonlar / Slack kartı (Slack Bağlayıcısı, Faz I dış bölüm 1,
// Task 4, spec 2026-08-10-slack-baglayicisi-design.md §4)
// ─────────────────────────────────────────────────────────────────────────

// GET v1/integrations/slack — SlackIntegrationStatusResponse (Dtos/SlackDtos.cs
// BİZZAT okunarak doğrulandı, t1/t3-report.md'ye GÜVENİLMEDİ): connected/
// teamId/teamName/recognitionChannelId/recognitionChannelName/isEnabled.
// Bağlı DEĞİLSE Status=true + connected=false döner (HATA DEĞİL — normal
// durum, task-1-report.md §1 "her yüklemede bu uç kart durumunu belirler").
// `isEnabled` BE'de her zaman GetStatusAsync'in `!integration.IsEnabled` erken
// dönüşü YÜZÜNDEN connected=true iken HER ZAMAN true'dur (disconnect edilmiş
// satır zaten connected=false döner) — FE yine de savunmacı okur (bkz.
// SlackIntegrationCard.js yorumu).
export const useSlackIntegrationApi = () => {
  const [status, setStatus] = useState(null);
  const [fetching, setFetching] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  useEffect(() => {
    setFetching(true);
    api.get('/v1/integrations/slack')
      .then((res) => setStatus(res.data?.data || { connected: false }))
      .catch(() => setStatus({ connected: false }))
      .finally(() => setFetching(false));
  }, [refreshTrigger]);

  // GET install-url — {url} (Slack authorize URL'i tam biçimiyle). Çağıran
  // taraf (SlackIntegrationCard) `window.location.href = url` ile yönlendirir
  // — SPA navigasyonu DEĞİL, tam sayfa geçişi (Slack'in kendi onay ekranı).
  const getInstallUrl = useCallback(async () => {
    try {
      const res = await api.get('/v1/integrations/slack/install-url');
      return res.data?.data?.url || null;
    } catch {
      return null;
    }
  }, []);

  const disconnect = useCallback(async () => {
    try {
      await api.delete('/v1/integrations/slack');
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  // PUT recognition-channel — yanıt AYNI SlackIntegrationStatusResponse
  // (t3-report.md §2 "ikinci GET gerekmez") — status state'i DOĞRUDAN bu
  // yanıttan güncellenir, ayrı bir refresh() ÇAĞRILMAZ.
  const setRecognitionChannel = useCallback(async (channelId, channelName) => {
    try {
      const res = await api.put('/v1/integrations/slack/recognition-channel', { channelId, channelName });
      setStatus(res.data?.data || null);
      return true;
    } catch {
      return false;
    }
  }, []);

  // POST test — SlackTestResult {status,error?}. `integrations.slack.testFailed`
  // (İÇ hata — decrypt/no-integration/no-email/network_error, t3-report.md §3)
  // KENDİ inline kutumuzda gösterilir, skipErrorToast ile interceptor'ın genel
  // toast'ı bastırılır (WebhooksTab.testEndpoint'in AYNI deseni).
  const testConnection = useCallback(async () => {
    try {
      const res = await api.post('/v1/integrations/slack/test', {}, {
        skipErrorToast: ['integrations.slack.testFailed'],
      });
      return { ok: true, data: res.data?.data };
    } catch (err) {
      return { ok: false, message: err.response?.data?.message };
    }
  }, []);

  return {
    status, fetching, refresh, getInstallUrl, disconnect, setRecognitionChannel, testConnection,
  };
};

// GET v1/integrations/slack/channels — SlackChannelResponse[] ({id,name}).
// Yalnız `enabled` true iken (bağlıyken) çekilir — bağlı değilken bu uç
// notConnected döner, gereksiz çağrı atılmaz.
//
// Final review (F2) — `.catch` GET'te ÖLÜ KOD (api.js interceptor'ı GET
// hatalarını ASLA reddetmez, satır 133-137: toast atar + `EMPTY_RESPONSE
// {data:{status:false,data:null}}` ile RESOLVE eder — bkz. memory
// reference_api_interceptor_never_rejects_reads). Gerçek bir arıza
// (`testFailed`/`notConnected`) bu yüzden `.then`'e her zaman BAŞARILI bir
// yanıt olarak düşerdi, `extractListData` boş dizi üretir, `error` hiç
// `true` olmazdı — `channelsErrorHint` üretimde asla görünmezdi. Gerçek
// sinyal `res.data?.status === false`'tan türetilir (interceptor'ın
// EMPTY_RESPONSE'unun KENDİ imzası); `.catch` yine de savunmacı olarak
// bırakıldı (network-seviyesi bir istisna teorik olarak mümkün).
export const useSlackChannels = (enabled) => {
  const [channels, setChannels] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  useEffect(() => {
    if (!enabled) {
      setChannels([]);
      setError(false);
      return;
    }
    setFetching(true);
    setError(false);
    api.get('/v1/integrations/slack/channels')
      .then((res) => {
        if (res.data?.status === false) {
          setError(true);
          setChannels([]);
        } else {
          setChannels(extractListData(res));
        }
      })
      .catch(() => setError(true))
      .finally(() => setFetching(false));
  }, [enabled, refreshTrigger]);

  return {
    channels, fetching, error, refresh,
  };
};
